import React, { useEffect, useState } from "react";
import api from "@/lib/api";

/**
 * Static brand-clean re-creation of the Thumbtack review widget.
 * Pulls the live review count from /api/social-proof/thumbtack (cached server-side 6h)
 * and falls back to a sensible hard-coded value if the API ever fails.
 * No third-party scripts — renders instantly with no flicker.
 */
const THUMBTACK_URL =
  "https://www.thumbtack.com/in/evansville/junk-removal/top-notch-pro-solutions/service/499495191399972872";
const THUMBTACK_WORDMARK =
  "https://cdn.thumbtackstatic.com/fe-assets-web/media/logos/thumbtack/wordmark.svg";
const THUMBTACK_STAR =
  "https://cdn.thumbtackstatic.com/fe-assets-web/media/pages/profile/standard-widgets/review-widget/orange_star.svg";

const FALLBACK_REVIEW_COUNT = 18;
const FALLBACK_RATING = 5;

// Module-level cache so the count is shared across every badge instance
// (testimonials, trust bar, footer) and survives client-side route changes.
let cachedStats = null;
let inflight = null;

async function loadStats() {
  if (cachedStats) return cachedStats;
  if (inflight) return inflight;
  inflight = api
    .get("/social-proof/thumbtack")
    .then((res) => {
      cachedStats = {
        reviewCount: res.data?.review_count ?? FALLBACK_REVIEW_COUNT,
        rating: res.data?.rating ?? FALLBACK_RATING,
      };
      return cachedStats;
    })
    .catch(() => {
      cachedStats = { reviewCount: FALLBACK_REVIEW_COUNT, rating: FALLBACK_RATING };
      return cachedStats;
    })
    .finally(() => {
      inflight = null;
    });
  return inflight;
}

function Stars({ size = 16, count = 5 }) {
  return (
    <div className="flex items-center gap-0.5" aria-label={`${count} out of 5 stars`}>
      {Array.from({ length: count }).map((_, i) => (
        <img
          key={i}
          src={THUMBTACK_STAR}
          alt=""
          width={size}
          height={size}
          loading="lazy"
          className="block"
          style={{ width: size, height: size }}
        />
      ))}
    </div>
  );
}

export default function ThumbtackBadge({ variant = "compact", className = "" }) {
  const [stats, setStats] = useState(
    cachedStats || { reviewCount: FALLBACK_REVIEW_COUNT, rating: FALLBACK_RATING }
  );

  useEffect(() => {
    let mounted = true;
    loadStats().then((s) => { if (mounted) setStats(s); });
    return () => { mounted = false; };
  }, []);

  const { reviewCount, rating } = stats;
  const label = `Top-Notch Pro Solutions on Thumbtack — ${rating} stars from ${reviewCount} reviews`;

  if (variant === "hero") {
    // Compact pill — designed to sit above the hero headline on dark imagery.
    return (
      <a
        href={THUMBTACK_URL}
        target="_blank"
        rel="noopener noreferrer"
        aria-label={label}
        data-testid="thumbtack-badge-hero"
        className={`inline-flex items-center gap-2 bg-white/95 backdrop-blur rounded-full pl-2 pr-3 py-1.5 shadow-lg hover:bg-white transition ${className}`}
      >
        <img src={THUMBTACK_WORDMARK} alt="Thumbtack" className="h-3.5 w-auto" loading="lazy" />
        <span className="w-px h-3.5 bg-slate-300" />
        <Stars size={12} count={rating} />
        <span className="text-[11px] font-bold text-slate-800 leading-none" data-testid="thumbtack-review-count-hero">
          {reviewCount} reviews
        </span>
      </a>
    );
  }

  if (variant === "featured") {
    return (
      <a
        href={THUMBTACK_URL}
        target="_blank"
        rel="noopener noreferrer"
        aria-label={label}
        data-testid="thumbtack-badge-featured"
        className={`group inline-flex items-center gap-4 bg-white border-2 border-[#FFA300]/40 hover:border-[#FFA300] rounded-2xl px-5 py-3.5 shadow-md hover:shadow-lg transition ${className}`}
      >
        <img src={THUMBTACK_WORDMARK} alt="Thumbtack" className="h-5 md:h-6 w-auto" loading="lazy" />
        <div className="w-px h-9 bg-[#e6e0d4]" />
        <div className="flex flex-col items-start gap-0.5">
          <div className="font-extrabold text-[var(--brand-teal)] text-sm leading-none" style={{ fontFamily: "Outfit" }}>
            Top-Notch Pro Solutions
          </div>
          <div className="flex items-center gap-2">
            <Stars size={18} count={rating} />
            <span className="text-xs font-semibold text-slate-600" data-testid="thumbtack-review-count-featured">
              {reviewCount} reviews
            </span>
          </div>
        </div>
      </a>
    );
  }

  if (variant === "footer") {
    return (
      <a
        href={THUMBTACK_URL}
        target="_blank"
        rel="noopener noreferrer"
        aria-label={label}
        data-testid="thumbtack-badge-footer"
        className={`group inline-flex items-center gap-3 bg-white/95 hover:bg-white rounded-xl px-3.5 py-2.5 transition shadow-sm ${className}`}
      >
        <img src={THUMBTACK_WORDMARK} alt="Thumbtack" className="h-4 w-auto" loading="lazy" />
        <div className="w-px h-6 bg-slate-200" />
        <div className="flex flex-col items-start leading-tight">
          <Stars size={13} count={rating} />
          <span className="text-[10px] font-bold uppercase tracking-wider text-slate-700 mt-0.5" data-testid="thumbtack-review-count-footer">
            {reviewCount} reviews
          </span>
        </div>
      </a>
    );
  }

  return (
    <a
      href={THUMBTACK_URL}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={label}
      data-testid="thumbtack-badge-compact"
      className={`group flex flex-col items-center gap-2 transition hover:opacity-90 ${className}`}
    >
      <img src={THUMBTACK_WORDMARK} alt="Thumbtack" className="h-5 w-auto" loading="lazy" />
      <div className="flex items-center gap-1.5">
        <Stars size={14} count={rating} />
        <span className="text-xs font-bold text-[var(--brand-teal)]" data-testid="thumbtack-review-count-compact">
          {reviewCount}
        </span>
      </div>
      <span className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">
        Verified Reviews
      </span>
    </a>
  );
}
